import { parseFoerderDatum, sortFoerderungenByDatum } from '../lib/foerderDatum';

export type FoerderEintrag = { id?: number; datum: string; instanz: string; vorgangsnummer: string; summe: string };

function parseEuro(s: string): number {
  const digits = (s || '').replace(/[^\d]/g, '');
  return digits ? Number(digits) : 0;
}

function within3Years(datum: string): boolean {
  const d = parseFoerderDatum(datum);
  if (!d) return false;
  const now = new Date();
  const cutoff = new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
  return d >= cutoff && d <= now;
}

export function FoerderListe({
  eintraege, grenze, darkMode, onChange,
}: {
  eintraege: FoerderEintrag[];
  grenze: number;
  darkMode?: boolean;
  onChange: (eintraege: FoerderEintrag[]) => void;
}) {
  // "Erhalten?" ist kein eigenes, separat zu pflegendes Feld mehr — es ergibt sich allein
  // daraus, ob unten mindestens eine Zeile steht (automatisch synct oder manuell
  // eingetragen). So kann das Häkchen nie mehr von der tatsächlichen Liste abweichen (Fall
  // Ralf König: 2 synchte Förderungen, Häkchen trotzdem aus, De-minimis-Prüfung lief nie).
  const erhalten = eintraege.length > 0;
  const summe3J = eintraege.filter(e => within3Years(e.datum)).reduce((s, e) => s + parseEuro(e.summe), 0);
  const ueber = summe3J > grenze;
  const txt = darkMode ? 'text-slate-100' : 'text-slate-800';

  const setRow = (idx: number, patch: Partial<FoerderEintrag>) =>
    onChange(eintraege.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const addRow = () =>
    onChange([...eintraege, { datum: '', instanz: 'BAFA', vorgangsnummer: '', summe: '' }]);
  const removeRow = (idx: number) => onChange(eintraege.filter((_, i) => i !== idx));

  // Gleiche Machart wie die übrigen hervorgehobenen Punkte im Formular: eigene Fläche
  // mit Rahmen statt eines linken Balkens ohne Hintergrund.
  return (
    <div className={`rounded-lg border p-3 mt-2 ${darkMode ? 'border-slate-600 bg-slate-800/50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-2.5" title="Ergibt sich automatisch aus der Liste unten — Zeile hinzufügen/entfernen ändert es.">
        <input type="checkbox" checked={erhalten} disabled readOnly
          className="w-4 h-4 text-purple-600 rounded-sm border-slate-300 cursor-default" />
        <span className={`text-sm font-semibold ${txt}`}>Förderung in den letzten 3 Jahren erhalten?</span>
      </div>

      <div className="mt-3 space-y-2">
        {/* Anzeige aufsteigend nach Datum (ältestes oben); Original-Index i bleibt für Edit/Remove. */}
        {sortFoerderungenByDatum(eintraege.map((e, i) => ({ e, i, datum: e.datum }))).map(({ e, i }) => (
          <div key={e.id ?? i} className="flex flex-wrap items-center gap-2">
            <input type="date" value={e.datum} onChange={ev => setRow(i, { datum: ev.target.value })}
              className="border rounded px-2 py-1 text-sm" aria-label="Datum" />
            <input type="text" placeholder="Instanz" value={e.instanz} onChange={ev => setRow(i, { instanz: ev.target.value })}
              className="border rounded px-2 py-1 text-sm w-28" />
            <input type="text" placeholder="Vorgangsnummer" value={e.vorgangsnummer} onChange={ev => setRow(i, { vorgangsnummer: ev.target.value })}
              className="border rounded px-2 py-1 text-sm w-40" />
            <input type="text" inputMode="numeric" placeholder="Summe €" value={e.summe} onChange={ev => setRow(i, { summe: ev.target.value })}
              className="border rounded px-2 py-1 text-sm w-28" />
            <button type="button" onClick={() => removeRow(i)} className="text-red-500 text-sm px-1" aria-label="Entfernen">✕</button>
          </div>
        ))}
        <button type="button" onClick={addRow} className="text-sm text-purple-600 font-medium">+ weitere Förderung</button>

        {erhalten && (
          <div className="text-sm mt-1">
            Summe (letzte 3 Jahre): <strong>{summe3J.toLocaleString('de-DE')} €</strong>
          </div>
        )}
        {ueber && (
          <div className="mt-1 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            ⚠️ De-minimis-Grenze überschritten — voraussichtlich nicht förderfähig.
          </div>
        )}
      </div>
    </div>
  );
}
